import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import { Alert, Checkbox, Divider } from '@apollo/orbit';
import React, { useMemo } from 'react';
import * as Yup from 'yup';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import Config from 'src/lib/config';
import { UserPermission } from 'src/lib/graphqlTypes/types';

import { editRolesValidationSchemaFragment } from './sharedValidation';
import { UserRolesSlider } from './UserRolesSlider';

export type PermissionValues = Yup.InferType<
  typeof editRolesValidationSchemaFragment
>;

export const CollectionPermissionsSection = ({
  graphRef,
  isEnterprise,
  minEditRole,
  setMinEditRole,
}: {
  graphRef: GraphRef;
  isEnterprise: boolean;
  minEditRole: UserPermission | null;
  setMinEditRole: (minEditRoles: UserPermission | null) => void;
}) => {
  const [advancedPermissionsExpanded, setAdvancedPermissionsExpanded] =
    React.useState(false);

  const selectedRoles = useMemo(() => {
    const rolesRanked: UserPermission[] = Config.orderedGraphRoles
      .slice()
      .reverse();

    const roleRank = minEditRole ? rolesRanked.indexOf(minEditRole) : 0;
    const roleDisplayNames = rolesRanked
      .slice(1)
      .map((val) => `${Config.roleNameByRoleEnum[val]}s`);

    return roleDisplayNames.slice(0, roleRank);
  }, [minEditRole]);

  return (
    <>
      <Divider className="-mx-6 mt-4 w-auto" />
      <Alert status="info" className="mt-4">
        <span className="font-semibold">All</span> users with access to{' '}
        {graphRef.graphId}@{graphRef.graphVariant} can view and run operations
        in this collection.
      </Alert>
      <div className="flex items-center">
        <ClickableText
          className="focus-visible:focus-ring flex items-center rounded border-none text-base text-link hover:no-underline"
          onClick={() =>
            setAdvancedPermissionsExpanded(!advancedPermissionsExpanded)
          }
        >
          {advancedPermissionsExpanded ? (
            <IconChevronDown className="mr-2" />
          ) : (
            <IconChevronRight className="mr-2" />
          )}
          Advanced permissions for this collection
        </ClickableText>
        <Tooltip label="You can change these permissions at any time. Admins can manage these permissions and create, update or delete this collection and its operations.">
          <IconInfo className="ml-2" />
        </Tooltip>
      </div>
      {advancedPermissionsExpanded &&
        (isEnterprise ? (
          <>
            <div className="min-h-10 text-sm text-neutral">
              <span className="font-semibold">Admins</span>
              {selectedRoles.map((val, index) => (
                <React.Fragment key={val}>
                  {index === selectedRoles.length - 1 ? ' and ' : ', '}
                  <span className="font-semibold">{val}</span>
                </React.Fragment>
              ))}{' '}
              on this graph can create, update or delete this collection and its
              operations:
            </div>
            <div className="mx-12">
              <UserRolesSlider
                minEditRole={minEditRole}
                setMinEditRole={setMinEditRole}
              />
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-neutral">
              Select whether to allow members of this organization to{' '}
              <span className="font-semibold">create</span>,{' '}
              <span className="font-semibold">update</span> or{' '}
              <span className="font-semibold">delete</span> this collection and
              its operations.
            </div>
            <div className="flex items-center text-sm">
              <Checkbox
                className="mr-3 size-4"
                isChecked={!!minEditRole}
                onChange={(e) => {
                  // Non enterprise all roles can edit
                  setMinEditRole(
                    e.target.checked ? UserPermission.CONSUMER : null,
                  );
                }}
              />
              Allow non admin members of this organization to create, update or
              delete
            </div>
          </>
        ))}
    </>
  );
};
